import React, { useState, useEffect } from "react";
import AOS from "aos";
import { FaEye, FaShoppingBag } from "react-icons/fa";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { getDatabase, ref, set, onValue } from "firebase/database";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown"; // Import ReactMarkdown
import remarkGfm from "remark-gfm"; // Untuk dukungan tabel, daftar, dll.

const CardProduct = ({ product }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [showFullDescription, setShowFullDescription] = useState(false); // State toggle deskripsi
    const navigate = useNavigate();

    useEffect(() => {
        AOS.init({
            duration: 500,
            once: false
        });

        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUser(user);
            setIsLoggedIn(!!user);
        });

        return () => unsubscribe();
    }, []);

    const toggleDescription = () => {
        setShowFullDescription(!showFullDescription);
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const handleAddToCart = () => {
        if (!isLoggedIn) {
            toast.warn(
                "Anda harus login terlebih dahulu untuk menambahkan ke keranjang!"
            );
            navigate("/login");
        } else {
            if (user) {
                const userId = auth.currentUser.uid;
                const db = getDatabase();

                // Cek status produk terlebih dahulu
                const productRef = ref(db, `products/${product.id}/status`);
                onValue(
                    productRef,
                    (snapshot) => {
                        const productStatus = snapshot.val();

                        if (!productStatus) {
                            toast.error("Produk tidak tersedia saat ini!");
                            return;
                        }

                        // Jika status produk tersedia, tambahkan ke keranjang
                        const userCartRef = ref(
                            db,
                            `cart/${userId}/${product.id}`
                        );
                        onValue(
                            userCartRef,
                            (snapshot) => {
                                const data = snapshot.val();
                                const currentCount = data ? data.count : 0;

                                set(userCartRef, {
                                    ...product,
                                    count: currentCount + 1
                                })
                                    .then(() => {
                                        toast.success(
                                            `Produk "${product.name}" berhasil ditambahkan ke keranjang!`
                                        );
                                    })
                                    .catch((error) => {
                                        console.error(
                                            "Gagal menambahkan ke keranjang:",
                                            error
                                        );
                                        toast.error(
                                            "Terjadi kesalahan saat menambahkan ke keranjang!"
                                        );
                                    });
                            },
                            { onlyOnce: true }
                        );
                    },
                    { onlyOnce: true }
                );
            }
        }
    };

    return (
        <>
            <div
                data-aos="fade-up"
                className="relative w-full sm:w-64 lg:w-80 xl:w-96 rounded-xl p-[2px] group"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-purple-500 to-red-500 rounded-xl p-[2px] animate-borderSpin group-hover:blur-sm"></div>
                <div className="relative bg-base-100 rounded-xl shadow-xl p-4 transition-transform duration-300 group-hover:scale-105">
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                        <span
                            className={`w-5 h-5 rounded-full ${
                                product.status
                                    ? "animate-blink-green"
                                    : "animate-blink-red"
                            }`}
                        ></span>
                    </div>
                    <div className="carousel rounded-box">
                        {product.images.map((image, index) => (
                            <div key={index} className="carousel-item w-full">
                                <img
                                    src={image.value}
                                    alt={`product-${index}`}
                                    className="rounded-lg w-full"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="card-body">
                        <h2 className="card-title">{product.name}</h2>
                        <p>
                            Rp {Number(product.price).toLocaleString("id-ID")}
                        </p>
                        <div className="card-actions justify-between items-center">
                            <button
                                className="btn btn-secondary"
                                onClick={handleAddToCart}
                            >
                                <FaShoppingBag /> Keranjang
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={openModal}
                            >
                                <FaEye /> Details
                            </button>
                        </div>
                    </div>
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
                            <button
                                onClick={closeModal}
                                className="absolute top-2 right-2 text-black text-2xl"
                            >
                                &times;
                            </button>
                            <figure className="mb-4">
                                <img
                                    src={product.images[0].value}
                                    alt="product"
                                    className="object-cover rounded-lg"
                                />
                            </figure>
                            <h2 className="text-2xl font-semibold mb-2">
                                {product.name}
                            </h2>
                            <div
                                className={`mb-4 text-sm text-gray-700 leading-relaxed prose ${
                                    product.description.length > 200
                                        ? "max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
                                        : ""
                                }`}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {showFullDescription ||
                                    product.description.length <= 200
                                        ? product.description
                                        : `${product.description.substring(
                                              0,
                                              200
                                          )}...`}
                                </ReactMarkdown>
                            </div>

                            {product.description.length > 200 && (
                                <button
                                    onClick={toggleDescription}
                                    className="text-blue-500 hover:underline text-sm"
                                >
                                    {showFullDescription
                                        ? "Tampilkan Lebih Sedikit"
                                        : "Baca Selanjutnya"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CardProduct;
